import {
    ButtonCancel,
    ButtonSecondary,
    Colors,
    Heading,
    Icon,
    SelectFilled,
    Text,
    type OptionProps,
} from '@amelio/ui-library';
import { useEffect, useMemo, useState } from 'react';

import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';

import { useGetPerformanceScoreLevelDeletionImpact } from '@/api/competency-services/performance-score-level';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

export type DeletePerformanceScoreLevelModalProps = {
    level: PerformanceScoreLevel;
    levels: PerformanceScoreLevel[];
    isDeleting: boolean;
    onClose: () => void;
    onConfirm: (replacementLevelId?: string) => void;
};

export const DeletePerformanceScoreLevelModal = ({
    level,
    levels,
    isDeleting,
    onClose,
    onConfirm,
}: DeletePerformanceScoreLevelModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const {
        data: impact,
        isFetching: isLoadingImpact,
        isError,
    } = useGetPerformanceScoreLevelDeletionImpact(level.id ?? null);

    const [replacementLevelId, setReplacementLevelId] = useState<string>('');

    useEffect(() => {
        setReplacementLevelId(impact?.recommendedReplacementLevel?.id ?? '');
    }, [impact?.recommendedReplacementLevel?.id]);

    const replacementOptions = useMemo<OptionProps[]>(
        () =>
            levels
                .filter((candidate): candidate is PerformanceScoreLevel & { id: string } =>
                    Boolean(candidate.id && candidate.id !== level.id),
                )
                .map((candidate) => ({
                    id: candidate.id,
                    value: candidate.id,
                    label: getTextByLanguage(candidate.label, language),
                })),
        [levels, level.id, language],
    );

    const hasAssignments = (impact?.affectedEvaluationCount ?? 0) > 0;

    const handleConfirm = () => {
        onConfirm(replacementLevelId || undefined);
    };

    const title = t('competencies.performanceScoreLevels.delete.title', {
        name: getTextByLanguage(level.label, language),
    });

    if (isLoadingImpact) {
        return (
            <div
                className={'flex flex-1 items-center justify-center gap-2 px-6 py-12'}
                data-testid={'performance-level-delete-loading'}
            >
                <div
                    className={
                        'border-base h-4 w-4 animate-spin rounded-full border-2 border-t-transparent'
                    }
                />
                <Text
                    size={'default'}
                    color={Colors.BASE_600}
                >
                    {t('competencies.cascade.loading')}
                </Text>
            </div>
        );
    }

    if (isError) {
        return (
            <div
                className={'flex flex-col gap-4'}
                data-testid={'performance-level-delete-forbidden'}
            >
                <Heading
                    level={4}
                    color={Colors.NAVY}
                >
                    <span className={'mb-2 flex items-center gap-2'}>
                        <Icon
                            name={'essential-danger'}
                            size={'md'}
                            color={Colors.ERROR}
                        />
                        <span>{t('errors.insufficientPermissionsTitle')}</span>
                    </span>
                </Heading>

                <Text size={'default'}>{t('errors.insufficientPermissionsMessage')}</Text>

                <div className={'mt-2 flex justify-end'}>
                    <ButtonSecondary
                        onClick={onClose}
                        size={'md'}
                        className={'sm:text-md text-sm'}
                        fitContent={true}
                    >
                        {t('common.close')}
                    </ButtonSecondary>
                </div>
            </div>
        );
    }

    return (
        <div className={'flex flex-col gap-4'}>
            <Heading
                level={4}
                color={Colors.NAVY}
            >
                <span className={'mb-2 flex items-center gap-2'}>
                    <Icon
                        name={'essential-warning-2'}
                        size={'md'}
                        color={Colors.WARNING}
                    />
                    <span>{title}</span>
                </span>
            </Heading>

            <div
                className={
                    'border-warning/50 bg-warning/10 flex flex-col gap-3 rounded-xl border p-4'
                }
            >
                <Text
                    size={'default'}
                    weight={'medium'}
                    color={Colors.WARNING}
                >
                    {t('competencies.performanceScoreLevels.delete.warning')}
                </Text>

                {hasAssignments && (
                    <Text size={'default'}>
                        <span className={'text-navy'}>
                            {t('competencies.performanceScoreLevels.delete.impactSummary', {
                                count: impact?.affectedEvaluationCount ?? 0,
                            })}
                        </span>
                    </Text>
                )}

                {impact?.hasNumericScores && hasAssignments && (
                    <Text size={'default'}>
                        <span className={'text-navy'}>
                            {t('competencies.performanceScoreLevels.delete.numericScoresNote')}
                        </span>
                    </Text>
                )}
            </div>

            <div className={'flex flex-col gap-2'}>
                <Text
                    size={'default'}
                    weight={'medium'}
                    color={Colors.NAVY}
                >
                    {t('competencies.performanceScoreLevels.delete.replaceWith')}
                </Text>

                <SelectFilled
                    value={replacementLevelId}
                    options={replacementOptions}
                    onChange={setReplacementLevelId}
                    disabled={isDeleting || !hasAssignments}
                    sort={'none'}
                    variant={'white'}
                    className={'[&>span]:!text-navy border-gray-100 [&>span]:!font-normal'}
                    chevronClassName={
                        '!h-4 !w-4 text-base-500 transition-colors duration-200 group-hover:text-primary group-disabled:text-base-300'
                    }
                    placeholder={t('competencies.performanceScoreLevels.delete.replacePlaceholder')}
                    dataTestId={'performance-level-delete-replacement'}
                />
            </div>

            <div className={'mt-2 flex justify-end gap-2'}>
                <ButtonSecondary
                    onClick={onClose}
                    disabled={isDeleting}
                    size={'md'}
                    className={'sm:text-md text-sm'}
                    fitContent={true}
                >
                    {t('common.cancel')}
                </ButtonSecondary>

                <ButtonCancel
                    onClick={handleConfirm}
                    loading={isDeleting}
                    disabled={isDeleting || !impact?.isDeletionAllowed}
                    size={'md'}
                    variant={'filled'}
                    className={'sm:text-md text-sm'}
                >
                    {t('competencies.performanceScoreLevels.delete.confirm')}
                </ButtonCancel>
            </div>
        </div>
    );
};
